import React from 'react';
import { AnimatePresence, LazyMotion, domAnimation } from 'motion/react';
import * as m from 'motion/react-m';

import { classNames } from '@payfit/growth-platform-brand-system-v2';

/**
 * Props for the AnimChangeChildren component
 * @property {React.ReactNode} children - React element to animate when props change
 * @property {string} as - HTML element type to render (default: 'div')
 * @property {string} className - Additional CSS classes
 * @property {number} durationOut - Duration in milliseconds for the exit animation
 * @property {number} durationIn - Duration in milliseconds for the entrance animation
 * @property {number} delayIn - Delay in milliseconds before starting the entrance animation (default: 0)
 */
type AnimChangeChildrenProps = {
  children: React.ReactNode;
  as?: 'div' | 'span' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  className?: string;
  initial?: {
    opacity?: number;
    y?: number;
  };
  animateTransition?: { duration?: number; delay?: number };
  exitTransition?: { duration?: number; delay?: number };
  exit?: {
    opacity?: number;
    y?: number;
  };
};

const hashStringSync = (str: string): string => {
  if (typeof window === 'undefined') return str;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash = hash & hash;
  }
  return Math.abs(hash).toString(16).substring(0, 6);
};

const getKey = (children: React.ReactNode): string => {
  if (React.isValidElement(children)) {
    const childContent = children.props.children;

    if (typeof childContent === 'string' || typeof childContent === 'number') {
      return hashStringSync(String(childContent));
    }

    const props = children.props;
    const key = children.key;

    const propsString =
      Object.entries(props)
        .filter(
          ([key, value]) =>
            key !== 'children' &&
            typeof value !== 'function' &&
            value !== undefined,
        )
        .map(([, value]) => JSON.stringify(value))
        .join('|') + `|${key}`;

    return hashStringSync(propsString);
  }
  return hashStringSync(String(children));
};

const defaultInitial = {
  opacity: 0,
  y: 0,
};
const defaultExit = {
  opacity: 0,
  y: 0,
};
const defaultAnimateTransition = {
  duration: 0.3,
  delay: 0,
};
const defaultExitTransition = {
  duration: 0.3,
  delay: 0,
};
export const AnimChangeChildren = ({
  children,
  className,
  initial = defaultInitial,
  exit = defaultExit,
  animateTransition = defaultAnimateTransition,
  exitTransition = defaultExitTransition,
}: AnimChangeChildrenProps) => {
  const [isFirstRender, setIsFirstRender] = React.useState(true);

  React.useEffect(() => {
    setIsFirstRender(false);
  }, []);

  return (
    <div className={classNames('relative', className)}>
      <div className="pointer-events-none invisible">{children}</div>
      <LazyMotion features={domAnimation}>
        <AnimatePresence>
          <m.div
            className="absolute inset-0"
            key={getKey(children)}
            initial={isFirstRender ? false : initial}
            animate={{ opacity: 1, y: 0, transition: animateTransition }}
            exit={{ ...exit, transition: exitTransition }}
          >
            {children}
          </m.div>
        </AnimatePresence>
      </LazyMotion>
    </div>
  );
};
